import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Typography,
  Box,
} from "@mui/material";
import Favorite from "@mui/icons-material/Favorite";
import FavoriteBorder from "@mui/icons-material/FavoriteBorder";
import CategoryIcon from "@mui/icons-material/Category";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ScheduleIcon from "@mui/icons-material/Schedule";
import FormatTime from "./FormatTime";
import {
  cardHover,
  cardOutline,
  fonts,
  primaryButtonStyles,
  secondaryButtonStyles,
  semiBoldFonts,
} from "../../Styles/Styles";

export default function SingleCard({ AdInfo }) {
  const navigate = useNavigate();
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    setLoggedInUser(user);
    if (user) {
      setFavorites(user.favorites || []);
    }
  }, []);

  const userId = loggedInUser ? loggedInUser._id : null;

  const handleViewAdClick = () => {
    navigate(`/ad/${AdInfo.uniqueId}`);
  };

  const handleMakeOffer = (uniqueId) => {
    navigate(`/ad/${uniqueId}`, { state: { showModal: true } });
  };

  const handleAddToFavorites = async () => {
    if (!userId) {
      alert("Please log in to add to favorites.");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/user/favorite`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ uniqueId: AdInfo.uniqueId }),
        }
      );
      const data = await response.json();
      if (response.ok) {
        const userResponse = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/user/singleuser/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const updatedUser = await userResponse.json();
        if (userResponse.ok) {
          localStorage.setItem("user", JSON.stringify(updatedUser));
          setLoggedInUser(updatedUser);
          setFavorites(updatedUser.favorites || []);
        }
        alert(data.message);
      } else {
        alert(data.error);
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  const isFavorite = favorites.includes(AdInfo.uniqueId);

  return (
    <Card
      sx={{
        maxWidth: "auto",
        position: "relative",
        mb: 2,
        ...cardOutline,
        ...cardHover,
      }}
    >
      {AdInfo.isSold && (
        <div
          style={{
            top: "0",
            position: "absolute",
            zIndex: 1,
            fontSize: "0.8rem",
            fontWeight: "700",
            backgroundColor: "#ff0019",
            color: "white",
            padding: "5px 10px",
          }}
        >
          Sold
        </div>
      )}
      <CardMedia
        sx={{
          height: 180,
          cursor: "pointer",
          backgroundColor: "#F1F3F4", // Black background for uncovered areas
          backgroundSize: "contain", // Ensure the image fits within the bounds while maintaining aspect ratio
          backgroundRepeat: "no-repeat", // Prevent the image from repeating
          backgroundPosition: "center", // Center the image
          backgroundImage: `url(${AdInfo.images[0]?.thumbnail})`, // Use the image as the background
        }}
        title={AdInfo.title}
        onClick={handleViewAdClick}
      />
      <CardContent>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Chip
            label={`$ ${AdInfo.price.toLocaleString()}`}
            sx={{
              ...semiBoldFonts,
              fontSize: "1rem",
              color: "white",
              borderRadius: "0",
              backgroundColor: "var(--primary-color)",
            }}
          />
          {isFavorite ? (
            <Favorite
              sx={{
                color: "var(--primary-color)",
                fontSize: "1.5rem",
                cursor: "pointer",
              }}
              onClick={handleAddToFavorites}
            />
          ) : (
            <FavoriteBorder
              sx={{
                color: "var(--primary-color)",
                fontSize: "1.5rem",
                cursor: "pointer",
              }}
              onClick={handleAddToFavorites}
            />
          )}
        </Box>

        <Typography
          gutterBottom
          variant="body1"
          component="div"
          onClick={handleViewAdClick}
          sx={{
            ...semiBoldFonts,
            cursor: "pointer",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            WebkitLineClamp: 2, // Limits the title to 2 lines
            lineHeight: "1.2em", // Adjust line height to ensure proper spacing
            maxHeight: "2.4em", // Maximum height for 2 lines based on line height
          }}
        >
          {AdInfo.title}
        </Typography>

        {/* Category and Location Section */}
        <Typography
          variant="body2"
          sx={{
            ...fonts,
            fontSize: "1em",
            color: "text.secondary",
            display: "flex",
            alignItems: "center",
            gap: "0.5em",
          }}
        >
          <CategoryIcon
            sx={{ fontSize: "1em", color: "var(--secondary-color)" }}
          />{" "}
          {/* Category Icon */}
          {AdInfo.category}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            ...fonts,
            fontSize: "1em",
            color: "text.secondary",
            display: "flex",
            alignItems: "center",
            gap: "0.5em",
          }}
        >
          <LocationOnIcon
            sx={{ fontSize: "1em", color: "var(--secondary-color)" }}
          />{" "}
          {/* Location Icon */}
          {AdInfo.location}
        </Typography>

        {/* Date Section */}
        <Typography
          variant="body2"
          sx={{
            ...fonts,
            fontSize: "1em",
            color: "text.secondary",
            display: "flex",
            alignItems: "center",
            gap: "0.5em",
          }}
        >
          <ScheduleIcon
            sx={{ fontSize: "1em", color: "var(--secondary-color)" }}
          />
          {/* Date Icon, can also use specific Material icons */}
          <FormatTime createdAt={AdInfo.createdAt} />
        </Typography>
      </CardContent>
      <CardActions
        sx={{
          pl: 2, // Add padding to the right if needed
          mb: 1, // Add padding to the right if needed
          marginTop: "-10px",
          justifyContent: "flex-start", // Align items to the right
        }}
      >
        <Button
          size="small"
          variant="contained"
          sx={{ ...primaryButtonStyles }}
          onClick={handleViewAdClick}
        >
          View ad
        </Button>
        {!AdInfo.isSold && AdInfo.receiveOffers === true && (
          <Button
            size="small"
            variant="contained"
            sx={{ ...secondaryButtonStyles }}
            onClick={() => handleMakeOffer(AdInfo.uniqueId)}
          >
            Make offer
          </Button>
        )}
      </CardActions>
    </Card>
  );
}
